import React, { useEffect, useMemo, useState } from "react";
import type {
  ActionBrief,
  ActionBriefSourceOpportunity,
  ProductArea,
} from "../../lib/actionBriefs";
import {
  getOrCreateActionBrief,
  markActionBriefCreated,
  logActionBriefEvent,
} from "../../lib/actionBriefApi";
import "./TorenControls.css";

type OpportunityActionBriefPanelProps = {
  accountId: string;
  runId?: string | null;
  productArea: ProductArea;
  opportunity: ActionBriefSourceOpportunity;
  accountContext?: {
    accountName?: string;
    category?: string;
    location?: string;
    competitors?: string[];
    differentiators?: string[];
  };
  initialActionBrief?: ActionBrief | null;
  onCompletionChange?: (updatedBrief: ActionBrief) => void;
};

type PanelState = "idle" | "generating" | "loaded" | "error";

function formatJsonList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((item) => String(item));
  }

  return [];
}

async function copyToClipboard(value: string): Promise<void> {
  if (!value) return;

  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.style.position = "fixed";
  textarea.style.left = "-9999px";
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
}

export default function OpportunityActionBriefPanel(
  props: OpportunityActionBriefPanelProps
) {
  const [panelState, setPanelState] = useState<PanelState>(props.initialActionBrief ? "loaded" : "idle");
  const [actionBrief, setActionBrief] = useState<ActionBrief | null>(props.initialActionBrief || null);
  const [copyStatus, setCopyStatus] = useState<string | null>(null);
  const [copiedTarget, setCopiedTarget] = useState<string | null>(null);

  const panelIdentity = useMemo(
  () =>
    [
      props.accountId,
      props.runId || "",
      props.productArea,
      props.opportunity.title,
      props.opportunity.category || "",
      props.opportunity.intent || "",
      props.opportunity.recommendation || "",
    ].join("|"),
  [
    props.accountId,
    props.runId,
    props.productArea,
    props.opportunity.title,
    props.opportunity.category,
    props.opportunity.intent,
    props.opportunity.recommendation,
  ]
);

useEffect(() => {
  setPanelState(props.initialActionBrief ? "loaded" : "idle");
  setActionBrief(props.initialActionBrief || null);
  setCopyStatus(null);
  setCopiedTarget(null);
}, [panelIdentity, props.initialActionBrief]);

  const contentBrief = actionBrief?.content_brief as
    | {
        objective?: string;
        audience?: string;
        key_points?: string[];
        suggested_faqs?: string[];
      }
    | undefined;

  const suggestedStructure = actionBrief?.suggested_structure as
    | {
        h1?: string;
        sections?: string[];
        seo_title?: string;
        meta_description?: string;
      }
    | undefined;

  const guardrails = useMemo(
    () => formatJsonList(actionBrief?.guardrails),
    [actionBrief]
  );

  const handleGenerate = async () => {
  setPanelState("generating");
  setCopyStatus(null);

  try {
    const result = await getOrCreateActionBrief({
      accountId: props.accountId,
      runId: props.runId ?? null,
      productArea: props.productArea,
      opportunity: props.opportunity,
      accountContext: props.accountContext,
    });

    setActionBrief(result.actionBrief);
    setPanelState("loaded");

    if (result.fromCache) {
      setCopyStatus(result.completionMemoryMatch ? "Related completed brief loaded" : "Saved brief loaded");
      window.setTimeout(() => setCopyStatus(null), 1800);
    }
  } catch (error) {
    console.error("[ActionBrief] Generation/load failed", error);
    setPanelState("error");
  }
};

  const handleCopy = async (label: string, value: string, target: string) => {
  await copyToClipboard(value);

  setCopyStatus(`${label} copied`);
  setCopiedTarget(target);

  try {
    if (actionBrief) {
      await logActionBriefEvent({
        actionBriefId: actionBrief.id,
        accountId: actionBrief.account_id,
        eventType: target === "prompt" ? "copied_prompt" : "copied_all",
        metadata: {
          product_area: props.productArea,
          copy_target: target,
        },
      });
    }
  } catch (error) {
    console.warn("[ActionBrief] Copy event log failed", error);
  }

  window.setTimeout(() => {
    setCopyStatus(null);
    setCopiedTarget(null);
  }, 1800);
};

  const handleCloseBrief = () => {
    setPanelState("idle");
    setActionBrief(null);
    setCopyStatus(null);
    setCopiedTarget(null);

  };

  const handleMarkCreated = async () => {
  if (!actionBrief) return;

  const isCurrentlyComplete = Boolean(actionBrief.marked_complete_at);

  try {
    const updatedBrief = await markActionBriefCreated(
      actionBrief.id,
      actionBrief.account_id,
      isCurrentlyComplete
    );

    setActionBrief(updatedBrief);
    props.onCompletionChange?.(updatedBrief);
    setCopyStatus(
      isCurrentlyComplete ? "Marked as not completed" : "Marked as completed"
    );

    window.setTimeout(() => setCopyStatus(null), 1800);
  } catch (error) {
    console.error("[ActionBrief] Toggle complete failed", error);
    setCopyStatus("Could not update completion status");
    window.setTimeout(() => setCopyStatus(null), 2200);
  }
};

  const fullBriefText = actionBrief
    ? [
        actionBrief.title,
        "",
        "Recommended Action",
        actionBrief.recommended_action || "",
        "",
        "Why This Matters",
        actionBrief.why_this_matters || "",
        "",
        "AI Writing Prompt",
        actionBrief.ai_writing_prompt,
        "",
        "Content Brief",
        JSON.stringify(actionBrief.content_brief, null, 2),
        "",
        "Suggested Structure",
        JSON.stringify(actionBrief.suggested_structure, null, 2),
        "",
        "Guardrails",
        guardrails.map((item) => `- ${item}`).join("\n"),
      ].join("\n")
    : "";

  if (panelState === "idle") {
  if (props.initialActionBrief?.marked_complete_at) {
    return (
      <div
        style={{
          marginTop: 16,
          border: "1px solid #bae6fd",
          background: "#f0f9ff",
          borderRadius: 16,
          padding: 16,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 900, letterSpacing: "0.12em", textTransform: "uppercase", color: "#0369a1" }}>
              Completed Action Brief
            </div>
            <div style={{ marginTop: 4, color: "#0f172a", fontSize: 13, fontWeight: 800 }}>
              Related completed work is available for review.
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              setActionBrief(props.initialActionBrief || null);
              setPanelState("loaded");
            }}
            className="toren-control toren-control--utility"
          >
            View brief
          </button>
        </div>
      </div>
    );
  }
  return (
    <div
      className="toren-surface"
      style={{ marginTop: 16 }}
    >
      <div
        style={{
          display: "flex",
          gap: 14,
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap"
        }}
      >
        <div style={{ flex: "1 1 420px" }}>
          <div
            style={{
              marginBottom: 4,
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "#0891b2"
            }}
          >
            Next step
          </div>
          <h4
            style={{
              margin: 0,
              fontSize: 16,
              fontWeight: 850,
              color: "#0f172a"
            }}
          >
            Build action plan
          </h4>
          <p
            style={{
              margin: "4px 0 0",
              fontSize: 13,
              lineHeight: 1.5,
              color: "#475569"
            }}
          >
            Organize this observation into an action plan. A CAI optimization brief is a separate, saved implementation brief; neither publishes website changes.
          </p>
        </div>

        <button
          type="button"
          onClick={handleGenerate}
          className="toren-control toren-control--primary"
        >
          Build action plan
        </button>
      </div>
    </div>
  );
}

  if (panelState === "generating") {
    return (
      <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4">
        <div className="animate-pulse">
          <div className="h-4 w-48 rounded bg-slate-200" />
          <div className="mt-3 h-3 w-full rounded bg-slate-200" />
          <div className="mt-2 h-3 w-5/6 rounded bg-slate-200" />
          <div className="mt-4 h-24 rounded bg-slate-200" />
        </div>
        <p className="mt-3 text-sm text-slate-600">
          Building action brief from this opportunity…
        </p>
      </div>
    );
  }

  if (panelState === "error") {
    return (
      <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4">
        <h4 className="text-sm font-semibold text-red-900">
          Couldn’t generate action brief
        </h4>
        <p className="mt-1 text-sm text-red-700">
          Try again or continue reviewing the opportunity.
        </p>
        <button
          type="button"
          onClick={handleGenerate}
          className="mt-3 rounded-md bg-red-700 px-4 py-2 text-sm font-medium text-white hover:bg-red-800"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (!actionBrief) {
    return null;
  }

  return (
  <div
    style={{
      marginTop: 16,
      border: "1px solid #bae6fd",
      background: "#f0f9ff",
      borderRadius: 14,
      padding: 16,
      boxShadow: "0 8px 20px rgba(14, 165, 233, 0.08)"
    }}
  >
    <div
      style={{
        display: "flex",
        gap: 14,
        alignItems: "flex-start",
        justifyContent: "space-between",
        flexWrap: "wrap",
        borderBottom: "1px solid #dbeafe",
        paddingBottom: 14
      }}
    >
      <div style={{ flex: "1 1 520px", minWidth: 0 }}>
        <p
          style={{
            margin: 0,
            fontSize: 11,
            fontWeight: 900,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: "#0369a1"
          }}
        >
          {actionBrief.marked_complete_at ? "Completed Action Brief" : "Generated Action Brief"}
        </p>
        <h4
          style={{
            margin: "4px 0 0",
            fontSize: 16,
            lineHeight: 1.3,
            fontWeight: 900,
            color: "#0f172a"
          }}
        >
          {actionBrief.title}
        </h4>
        <p
          style={{
            margin: "6px 0 0",
            fontSize: 12,
            color: "#64748b"
          }}
        >
          {actionBrief.marked_complete_at
            ? `Related competitive work was completed on ${new Date(actionBrief.marked_complete_at).toLocaleDateString()}. This opportunity remains visible because the current run still shows the underlying visibility gap.`
            : "Saved to the account when generated. Full AI generation will connect next."}
        </p>
        {copyStatus && (
          <p
            style={{
              margin: "8px 0 0",
              fontSize: 13,
              fontWeight: 800,
              color: "#047857"
            }}
          >
            ✓ {copyStatus}
          </p>
        )}
      </div>

      <button
        type="button"
        onClick={() => handleCopy("Brief", fullBriefText, "all")}
        className="toren-control toren-control--utility"
      >
        {copiedTarget === "all" ? "✓ Copied" : "⧉ Copy All"}
      </button>
    </div>

    <div style={{ display: "grid", gap: 14, marginTop: 14 }}>
      <section
        style={{
          border: "1px solid #dbeafe",
          background: "#ffffff",
          borderRadius: 12,
          padding: 14
        }}
      >
        <h5
          style={{
            margin: 0,
            fontSize: 13,
            fontWeight: 900,
            color: "#0f172a"
          }}
        >
          Recommended Action
        </h5>
        <p
          style={{
            margin: "6px 0 0",
            fontSize: 13,
            lineHeight: 1.6,
            color: "#334155"
          }}
        >
          {actionBrief.recommended_action}
        </p>
      </section>

      <section
        style={{
          border: "1px solid #dbeafe",
          background: "#ffffff",
          borderRadius: 12,
          padding: 14
        }}
      >
        <h5
          style={{
            margin: 0,
            fontSize: 13,
            fontWeight: 900,
            color: "#0f172a"
          }}
        >
          Why This Matters
        </h5>
        <p
          style={{
            margin: "6px 0 0",
            fontSize: 13,
            lineHeight: 1.6,
            color: "#334155"
          }}
        >
          {actionBrief.why_this_matters}
        </p>
      </section>

      <section
        style={{
          border: "1px solid #1e293b",
          background: "#0f172a",
          borderRadius: 12,
          overflow: "hidden"
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "center",
            justifyContent: "space-between",
            padding: "11px 14px",
            borderBottom: "1px solid #334155",
            background: "#111827"
          }}
        >
          <h5
            style={{
              margin: 0,
              fontSize: 13,
              fontWeight: 900,
              color: "#e5e7eb"
            }}
          >
            Copy-Ready AI Writing Prompt
          </h5>
        </div>

        <pre
          style={{
            margin: 0,
            padding: 14,
            whiteSpace: "pre-wrap",
            fontSize: 13,
            lineHeight: 1.65,
            color: "#f8fafc",
            fontFamily:
              'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
            overflowX: "auto"
          }}
        >
          {actionBrief.ai_writing_prompt}
        </pre>
      </section>

      <section
        style={{
          border: "1px solid #dbeafe",
          background: "#ffffff",
          borderRadius: 12,
          padding: 14
        }}
      >
        <h5
          style={{
            margin: 0,
            fontSize: 13,
            fontWeight: 900,
            color: "#0f172a"
          }}
        >
          Content Brief
        </h5>

        <div
          style={{
            marginTop: 8,
            fontSize: 13,
            lineHeight: 1.6,
            color: "#334155"
          }}
        >
          {contentBrief?.objective && (
            <p style={{ margin: 0 }}>
              <strong>Objective:</strong> {contentBrief.objective}
            </p>
          )}

          {contentBrief?.audience && (
            <p style={{ margin: "8px 0 0" }}>
              <strong>Audience:</strong> {contentBrief.audience}
            </p>
          )}

          {Array.isArray(contentBrief?.key_points) && (
            <div style={{ marginTop: 10 }}>
              <p style={{ margin: 0, fontWeight: 800 }}>Key points:</p>
              <ul style={{ margin: "4px 0 0", paddingLeft: 20 }}>
                {contentBrief.key_points.map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
            </div>
          )}

          {Array.isArray(contentBrief?.suggested_faqs) && (
            <div style={{ marginTop: 10 }}>
              <p style={{ margin: 0, fontWeight: 800 }}>Suggested FAQs:</p>
              <ul style={{ margin: "4px 0 0", paddingLeft: 20 }}>
                {contentBrief.suggested_faqs.map((faq) => (
                  <li key={faq}>{faq}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </section>

      <section
        style={{
          border: "1px solid #dbeafe",
          background: "#ffffff",
          borderRadius: 12,
          padding: 14
        }}
      >
        <h5
          style={{
            margin: 0,
            fontSize: 13,
            fontWeight: 900,
            color: "#0f172a"
          }}
        >
          Suggested Structure
        </h5>

        <div
          style={{
            marginTop: 8,
            fontSize: 13,
            lineHeight: 1.6,
            color: "#334155"
          }}
        >
          {suggestedStructure?.h1 && (
            <p style={{ margin: 0 }}>
              <strong>H1:</strong> {suggestedStructure.h1}
            </p>
          )}

          {suggestedStructure?.seo_title && (
            <p style={{ margin: "8px 0 0" }}>
              <strong>SEO title:</strong> {suggestedStructure.seo_title}
            </p>
          )}

          {suggestedStructure?.meta_description && (
            <p style={{ margin: "8px 0 0" }}>
              <strong>Meta description:</strong>{" "}
              {suggestedStructure.meta_description}
            </p>
          )}

          {Array.isArray(suggestedStructure?.sections) && (
            <div style={{ marginTop: 10 }}>
              <p style={{ margin: 0, fontWeight: 800 }}>Sections:</p>
              <ul style={{ margin: "4px 0 0", paddingLeft: 20 }}>
                {suggestedStructure.sections.map((section) => (
                  <li key={section}>{section}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </section>

      {guardrails.length > 0 && (
        <section
          style={{
            border: "1px solid #fde68a",
            background: "#fffbeb",
            borderRadius: 12,
            padding: 14
          }}
        >
          <h5
            style={{
              margin: 0,
              fontSize: 13,
              fontWeight: 900,
              color: "#92400e"
            }}
          >
            Guardrails
          </h5>
          <ul
            style={{
              margin: "8px 0 0",
              paddingLeft: 20,
              fontSize: 13,
              lineHeight: 1.6,
              color: "#78350f"
            }}
          >
            {guardrails.map((guardrail) => (
              <li key={guardrail}>{guardrail}</li>
            ))}
          </ul>
        </section>
      )}

      <div
        style={{
          borderTop: "1px solid #dbeafe",
          paddingTop: 12,
          display: "flex",
          gap: 10,
          flexWrap: "wrap",
          alignItems: "center"
        }}
      >
        <button
          type="button"
          onClick={handleMarkCreated}
          disabled={false}
          style={{
            border: actionBrief.marked_complete_at
              ? "1px solid #bbf7d0"
              : "1px solid #cbd5e1",
            borderRadius: 10,
            background: actionBrief.marked_complete_at ? "#dcfce7" : "#ffffff",
            color: actionBrief.marked_complete_at ? "#166534" : "#334155",
            fontSize: 13,
            fontWeight: 800,
            padding: "9px 12px",
            cursor: "pointer"
          }}
        >
          {actionBrief.marked_complete_at ? "Mark as incomplete" : "Mark as completed"}
        </button>

        <button
          type="button"
          onClick={handleCloseBrief}
          className="toren-control toren-control--tertiary"
        >
          Close brief
        </button>
      </div>
    </div>
  </div>
);
}
